import { createAsyncThunk } from '@reduxjs/toolkit';
import { studyYearCategoriesService } from '@/services/study-year-categories.service';
import { AppDispatch, RootState } from '@/store';
import {
  GetStudyYearCategoryRequest,
  GetStudyYearCategoriesRequest,
  GetStudyYearCategoryResponse,
  GetStudyYearCategoriesResponse,
  UpdateStudyYearCategoryRequest,
  UpdateStudyYearCategoryResponse,
  GetStudyYearCategoryDataResponse,
  GetStudyYearCategoryForecastsResponse,
  GetChildCategoriesResponse,
  GetChildCategoriesDataResponse,
  GetCategoryCompaniesResponse,
  GetParentCategoryResponse,
  AddChildCategoryRequest,
  UpdateCategoryForecastsRequest,
  DeleteChildCategoryRequest,
} from '@/types/study-year-category';
import { showSnackbar } from '../snakebar/slice';

// NEW: Get all study year categories with filtering
const getStudyYearCategories = createAsyncThunk<
  GetStudyYearCategoriesResponse,
  GetStudyYearCategoriesRequest | undefined
>('studyYearCategories/getStudyYearCategories', async (params, { rejectWithValue }) => {
  try {
    return await studyYearCategoriesService.getStudyYearCategories(params);
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch study year categories'
    );
  }
});

// NEW: Get study year categories (new endpoint)
const getStudyYearCategoriesNew = createAsyncThunk<
  GetStudyYearCategoriesResponse,
  GetStudyYearCategoriesRequest | undefined
>('studyYearCategories/getStudyYearCategoriesNew', async (params, { rejectWithValue }) => {
  try {
    return await studyYearCategoriesService.getStudyYearCategoriesNew(params);
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch study year categories (new)'
    );
  }
});

const getStudyYearCategory = createAsyncThunk<
  GetStudyYearCategoryResponse,
  GetStudyYearCategoryRequest
>('studyYearCategories/getStudyYearCategory', async (request, { rejectWithValue }) => {
  try {
    return await studyYearCategoriesService.getStudyYearCategory(request);
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch study year category');
  }
});

const updateStudyYearCategory = createAsyncThunk<
  UpdateStudyYearCategoryResponse,
  UpdateStudyYearCategoryRequest,
  { dispatch: AppDispatch; state: RootState }
>('studyYearCategories/updateStudyYearCategory', async (request, { dispatch, rejectWithValue }) => {
  try {
    console.log('Updating study year category:', request);
    const response = await studyYearCategoriesService.updateStudyYearCategory(request);
    dispatch(
      showSnackbar({
        message: 'Study year category updated successfully!',
        variant: 'success',
      })
    );
    console.log('Update response:', response);
    return response;
  } catch (error: any) {
    console.error('Error updating study year category:', error);
    dispatch(
      showSnackbar({
        message: error instanceof Error ? error.message : 'Failed to update category',
        variant: 'error',
      })
    );
    
    return rejectWithValue(error.response?.data?.message || 'Failed to update study year category');
  }
});

// NEW: Get study year category forecasts
const getStudyYearCategoryForecasts = createAsyncThunk<
  GetStudyYearCategoryForecastsResponse,
  GetStudyYearCategoryRequest
>('studyYearCategories/getStudyYearCategoryForecasts', async (request, { rejectWithValue }) => {
  try {
    return await studyYearCategoriesService.getStudyYearCategoryForecasts(request);
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch category forecasts');
  }
});

const getChildCategoriesData = createAsyncThunk<
  GetChildCategoriesDataResponse,
  GetStudyYearCategoryRequest
>('studyYearCategories/getChildCategoriesData', async (request, { rejectWithValue }) => {
  try {
    return await studyYearCategoriesService.getChildCategoriesData(request);
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch child categories data'
    );
  }
});

const getStudyYearCategoryData = createAsyncThunk<
  GetStudyYearCategoryDataResponse,
  GetStudyYearCategoryRequest
>('studyYearCategories/getStudyYearCategoryData', async (request, { rejectWithValue }) => {
  try {
    return await studyYearCategoriesService.getStudyYearCategoryData(request);
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch category data');
  }
});

const getChildCategories = createAsyncThunk<
  GetChildCategoriesResponse,
  GetStudyYearCategoryRequest
>('studyYearCategories/getChildCategories', async (request, { rejectWithValue }) => {
  try {
    return await studyYearCategoriesService.getChildCategories(request);
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch child categories');
  }
});

const addChildCategory = createAsyncThunk<
  void,
  AddChildCategoryRequest,
  { dispatch: AppDispatch; state: RootState }
>('studyYearCategories/addChildCategory', async (request, { dispatch, rejectWithValue }) => {
  try {
    await studyYearCategoriesService.addChildCategory(request);
    dispatch(
      showSnackbar({
        message: 'Child category added successfully!',
        variant: 'success',
      })
    );
  } catch (error: any) {
    dispatch(
      showSnackbar({
        message: error instanceof Error ? error.message : 'Failed to add child category',
        variant: 'error',
      })
    );
    return rejectWithValue(error.response?.data?.message || 'Failed to add child category');
  }
});

// NEW: Get parent category
const getParentCategory = createAsyncThunk<GetParentCategoryResponse, GetStudyYearCategoryRequest>(
  'studyYearCategories/getParentCategory',
  async (request, { rejectWithValue }) => {
    try {
      return await studyYearCategoriesService.getParentCategory(request);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch parent category');
    }
  }
);



// NEW: Delete child category
const deleteChildCategory = createAsyncThunk<
  void,
  DeleteChildCategoryRequest,
  { dispatch: AppDispatch; state: RootState }
>('studyYearCategories/deleteChildCategory', async (request, { dispatch, rejectWithValue }) => {
  try {
    await studyYearCategoriesService.deleteChildCategory(request);
    dispatch(
      showSnackbar({
        message: 'Child category deleted successfully!',
        variant: 'success',
      })
    );
  } catch (error: any) {
    dispatch(
      showSnackbar({
        message: error instanceof Error ? error.message : 'Failed to delete child category',
        variant: 'error',
      })
    );
    return rejectWithValue(error.response?.data?.message || 'Failed to delete child category');
  }
});

const getCategoryCompanies = createAsyncThunk<
  GetCategoryCompaniesResponse,
  GetStudyYearCategoryRequest
>('studyYearCategories/getCategoryCompanies', async (request, { rejectWithValue }) => {
  try {
    return await studyYearCategoriesService.getCategoryCompanies(request);
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch category companies');
  }
});

const updateCategoryForecasts = createAsyncThunk<
  void,
  UpdateCategoryForecastsRequest,
  { dispatch: AppDispatch; state: RootState }
>('studyYearCategories/updateCategoryForecasts', async (request, { dispatch, rejectWithValue }) => {
  try {
    await studyYearCategoriesService.updateCategoryForecasts(request);
    dispatch(
      showSnackbar({
        message: 'Category forecasts updated successfully!',
        variant: 'success',
      })
    );
  } catch (error: any) {
    dispatch(
      showSnackbar({
        message: error instanceof Error ? error.message : 'Failed to update forecasts',
        variant: 'error',
      })
    );
    return rejectWithValue(error.response?.data?.message || 'Failed to update category forecasts');
  }
});

export const studyYearCategories = {
  // NEW endpoints
  getStudyYearCategories,
  getStudyYearCategoriesNew,
  getStudyYearCategoryForecasts,
  getParentCategory,
  deleteChildCategory,
  getStudyYearCategory,
  updateStudyYearCategory,
  getStudyYearCategoryData,
  getChildCategories,
  addChildCategory,
  getChildCategoriesData,
  getCategoryCompanies,
  updateCategoryForecasts,
};
